import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import authService from '../../services/authService';
import { ModuleWithPosts, Post } from '../../types/auth';
import PostModal from '../../components/PostModal/PostModal';
import Navigation from '../../components/Navigation/Navigation';
import './ModuleDetailsPage.css';

const ModuleDetailsPage: React.FC = () => {
  const { questId, moduleId } = useParams<{
    questId: string;
    moduleId: string;
  }>();

  const [currentModule, setCurrentModule] = useState<ModuleWithPosts | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);

  useEffect(() => {
    const fetchQuestDetails = async () => {
      if (!questId) return;

      try {
        setLoading(true);
        const data = await authService.getQuestDetails(questId);

        // Find the specific module
        const module = data.modules.find(m => m.module.id === moduleId);
        if (module) {
          setCurrentModule(module);
        } else {
          setError('Module not found');
        }
      } catch (err) {
        console.error('Failed to fetch quest details:', err);
        setError('Failed to load module details');
      } finally {
        setLoading(false);
      }
    };

    fetchQuestDetails();
  }, [questId, moduleId]);

  const handleEditPost = (post: Post) => {
    setEditingPost(post);
    setShowPostModal(true);
  };

  const handleAddPost = () => {
    setEditingPost(null);
    setShowPostModal(true);
  };

  const handlePostModalClose = () => {
    setShowPostModal(false);
    setEditingPost(null);
  };

  const handlePostSave = async (postData: Post) => {
    try {
      if (editingPost) {
        await authService.updatePost(editingPost.id, postData);
        console.log('Post updated successfully');

        // Update post-quest-module mapper
        try {
          await authService.updatePostQuestModuleMapper(
            editingPost.id,
            questId!,
            moduleId!,
            postData.post_order || 1
          );
          console.log('Post-quest-module mapper updated successfully');
        } catch (mapperError) {
          console.error(
            'Failed to update post-quest-module mapper (non-critical):',
            mapperError
          );
          // Don't throw error here as mapper update failure shouldn't break post update
        }

        // Push data to SQS queue after successful post update
        try {
          await authService.pushToSQS({
            post_id: editingPost.id,
            video_url: postData.video_url || '',
            image_url: postData.image_url || '',
            thumbnail: postData.thumbnail || '',
            audio_url: postData.audio_url || '',
            lrc_url: postData.lrc_url || '',
            entity_type: 'quest_post',
          });
          console.log('Data pushed to SQS successfully after post update');
        } catch (sqsError) {
          console.error('Failed to push data to SQS (non-critical):', sqsError);
          // Don't throw error here as SQS push failure shouldn't break post update
        }

        // Update local state with new update_at timestamp
        if (currentModule) {
          setCurrentModule({
            ...currentModule,
            posts: currentModule.posts.map(p =>
              p.id === editingPost.id
                ? { ...p, ...postData, update_at: new Date().toISOString() }
                : p
            ),
          });
        }
      } else {
        // Create new post
        const { id, user_id, ...createData } = postData;
        const createdPost = await authService.createPost({
          ...createData,
          module_id: moduleId!,
        });
        console.log('Post created successfully:', createdPost);

        // Create post-quest-module mapper
        await authService.createPostQuestModuleMapper(
          createdPost.id,
          questId!,
          moduleId!,
          postData.post_order || 1
        );
        console.log('Post-quest-module mapper created successfully');

        // Push data to SQS queue (only if URLs don't contain "_compressed")
        try {
          await authService.pushToSQS({
            post_id: createdPost.id,
            video_url: createdPost.video_url || '',
            image_url: createdPost.image_url || '',
            thumbnail: createdPost.thumbnail || '',
            audio_url: createdPost.audio_url || '',
            lrc_url: createdPost.lrc_url || '',
            entity_type: 'quest_post',
          });
          console.log('Data pushed to SQS successfully');
        } catch (sqsError) {
          console.error('Failed to push data to SQS (non-critical):', sqsError);
          // Don't throw error here as SQS push failure shouldn't break post creation
        }
      }

      // Refresh the module data
      const data = await authService.getQuestDetails(questId!);
      const module = data.modules.find(m => m.module.id === moduleId);
      if (module) {
        setCurrentModule(module);
      }

      handlePostModalClose();
    } catch (error) {
      console.error('Failed to save post:', error);
      setError('Failed to save post. Please try again.');
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      await authService.deletePostQuestModuleMapper(moduleId!, postId);
      console.log('Post deleted successfully');

      // Refresh the module data
      const data = await authService.getQuestDetails(questId!);
      const module = data.modules.find(m => m.module.id === moduleId);
      if (module) {
        setCurrentModule(module);
      }
    } catch (error) {
      console.error('Failed to delete post:', error);
      setError('Failed to delete post. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="module-details-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading module details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="module-details-page">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-details-page">
        <div className="error-message">
          <h2>Module Not Found</h2>
          <p>The requested module could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="module-details-page">
      <Navigation backTo={`/quests/${questId}`} />
      <div className="module-details-header">
        <h1 className="page-title">Module Details</h1>
        <p className="page-subtitle">
          Quest ID: {questId} | Module ID: {moduleId}
        </p>
      </div>

      <div className="module-details-content">
        {/* Module Info Section */}
        <div className="module-info-section">
          <div className="module-card-large">
            <div className="module-image">
              {currentModule.module.image_url ? (
                <img
                  src={currentModule.module.image_url}
                  alt={currentModule.module.title}
                />
              ) : (
                <div className="module-image-placeholder">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21,15 16,10 5,21" />
                  </svg>
                </div>
              )}
            </div>
            <div className="module-content">
              <h2 className="module-title">{currentModule.module.title}</h2>
              <p className="module-subtitle">{currentModule.module.subtitle}</p>
              <p className="module-description">
                {currentModule.module.description}
              </p>
              <div className="module-meta">
                <span className="module-duration">
                  {currentModule.module.duration} min
                </span>
                <span className="module-contents">
                  {currentModule.module.no_of_contents} contents
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Posts Section */}
        <div className="posts-section">
          <div className="posts-header">
            <h2 className="section-title">Posts</h2>
            <div className="posts-actions">
              <span className="posts-count">
                {currentModule.posts.length} posts
              </span>
              <button className="add-post-button" onClick={handleAddPost}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Post
              </button>
            </div>
          </div>

          {currentModule.posts.length === 0 ? (
            <div className="no-posts">
              <p>No posts available for this module.</p>
            </div>
          ) : (
            <div className="posts-grid">
              {currentModule.posts.map((post: Post) => (
                <div key={post.id} className="post-card">
                  <div className="post-image">
                    {post.thumbnail ? (
                      <img src={post.thumbnail} alt={post.attributes.title} />
                    ) : (
                      <div className="post-image-placeholder">
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21,15 16,10 5,21" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="post-content">
                    <h3 className="post-title">{post.attributes.title}</h3>
                    <p className="post-text">{post.text}</p>
                    {(post.created_at || post.update_at) && (
                      <div className="post-timestamps">
                        {post.created_at && (
                          <span className="post-timestamp">
                            Created{' '}
                            {new Date(post.created_at).toLocaleDateString(
                              'en-US',
                              {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              }
                            )}
                          </span>
                        )}
                        {post.update_at && (
                          <span className="post-timestamp">
                            Updated{' '}
                            {new Date(post.update_at).toLocaleDateString(
                              'en-US',
                              {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              }
                            )}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="post-meta">
                      <div className="post-info">
                        <span className="post-type">{post.post_type}</span>
                        <span className="post-language">
                          {post.post_language}
                        </span>
                      </div>
                      <div className="post-actions">
                        <button
                          className="edit-post-button"
                          onClick={e => {
                            e.stopPropagation();
                            handleEditPost(post);
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                          Edit
                        </button>
                        <button
                          className="delete-post-button"
                          onClick={e => {
                            e.stopPropagation();
                            handleDeletePost(post.id);
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <PostModal
        post={editingPost}
        isOpen={showPostModal}
        onClose={handlePostModalClose}
        onSave={handlePostSave}
      />
    </div>
  );
};

export default ModuleDetailsPage;
